import { dmChannel } from "../../../shared/chat.js";
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { isValidName, sanitizeName } from "../../utils/name";
import {
  drawCard,
  drawPillButton,
  drawSceneBackground,
  drawSceneTitle,
  fillRoundedRect,
  strokeRoundedRect,
  truncateToWidth,
} from "../../utils/draw";
import type { Button } from "../systems/Buttons";
import { backButton } from "../systems/Buttons";
import type { FriendInfo } from "../systems/Friends";
import { BattlesScene } from "./BattlesScene";
import { ChatScene } from "./ChatScene";
import { MenuScene } from "./MenuScene";
import { RaceScene } from "./RaceScene";
import type { GameContext, Scene } from "./Scene";

const CARD_X = 20;
const CARD_W = 320;
const ROW_H = 52;
const ROW_GAP = 6;
const LIST_TOP = 150;
const LIST_BOTTOM = 600;
const LIST_H = LIST_BOTTOM - LIST_TOP;

type Row =
  | { kind: "in"; name: string }
  | { kind: "out"; name: string }
  | { kind: "friend"; name: string; info: FriendInfo };

interface RowButton {
  rect: { x: number; y: number; w: number; h: number };
  label: string;
  color: string;
  run: () => void;
}

export class FriendsScene implements Scene {
  private scrollY = 0;
  private maxScroll = 0;
  private dragLastY: number | null = null;
  private pollAccum = 0;
  private polling = false;
  private acting = false;
  private entered = false;

  constructor(private readonly ctxMgr: GameContext) {}

  private get myName(): string {
    return this.ctxMgr.settings.playerName;
  }
  private get token(): string {
    return this.ctxMgr.settings.authToken;
  }

  enter(): void {
    this.entered = true;
    void this.reload();
    const canvas = this.ctxMgr.renderer.canvas;
    canvas.addEventListener("wheel", this.onWheel, { passive: false });
    canvas.addEventListener("pointerdown", this.onDragStart);
    window.addEventListener("pointermove", this.onDragMove);
    window.addEventListener("pointerup", this.onDragEnd);
    this.rebuild();
  }

  exit(): void {
    this.entered = false;
    this.ctxMgr.buttons.clear();
    const canvas = this.ctxMgr.renderer.canvas;
    canvas.removeEventListener("wheel", this.onWheel);
    canvas.removeEventListener("pointerdown", this.onDragStart);
    window.removeEventListener("pointermove", this.onDragMove);
    window.removeEventListener("pointerup", this.onDragEnd);
  }

  update(dt: number): void {
    // Re-poll presence + requests + invites a couple times a second so online
    // dots and incoming race invites stay live while the screen is open.
    this.pollAccum += dt;
    if (this.pollAccum >= 2.5 && !this.polling && !this.acting) {
      this.pollAccum = 0;
      void this.reload();
    }
  }

  private async reload(): Promise<void> {
    if (!this.ctxMgr.settings.isLoggedIn()) return;
    this.polling = true;
    await this.ctxMgr.friends.refresh(this.myName, this.token);
    this.polling = false;
    if (this.entered) this.rebuild();
  }

  private rows(): Row[] {
    const f = this.ctxMgr.friends;
    const out: Row[] = [];
    for (const n of f.incomingRequests()) out.push({ kind: "in", name: n });
    for (const info of f.list()) out.push({ kind: "friend", name: info.name, info });
    for (const n of f.outgoingRequests()) out.push({ kind: "out", name: n });
    return out;
  }

  // The action buttons for a row, with explicit rects so render() and rebuild()
  // agree on positions. Friends get [unfriend ×][Battle][Race/Join].
  private rowButtons(row: Row, rowY: number): RowButton[] {
    const cy = rowY + (ROW_H - 24) / 2;
    const right = CARD_X + CARD_W - 10;
    if (row.kind === "in") {
      const decline = { x: right - 66, y: cy, w: 66, h: 24 };
      const accept = { x: decline.x - 6 - 64, y: cy, w: 64, h: 24 };
      return [
        { rect: accept, label: "Accept", color: "#2e7d32", run: () => void this.act("accept", row.name) },
        { rect: decline, label: "Decline", color: "#8e1a1a", run: () => void this.act("decline", row.name) },
      ];
    }
    if (row.kind === "out") {
      const cancel = { x: right - 66, y: cy, w: 66, h: 24 };
      return [
        { rect: cancel, label: "Cancel", color: "#8e1a1a", run: () => void this.act("cancel", row.name) },
      ];
    }
    const race = { x: right - 62, y: cy, w: 62, h: 24 };
    const battle = { x: race.x - 5 - 58, y: cy, w: 58, h: 24 };
    const unfriend = { x: battle.x - 5 - 22, y: cy, w: 22, h: 24 };
    const joining = row.info.inviteFromThem;
    return [
      { rect: unfriend, label: "×", color: "#5a3030", run: () => void this.act("remove", row.name) },
      { rect: battle, label: "Battle", color: "#7a5e2a", run: () => void this.doBattle(row.name) },
      {
        rect: race,
        label: joining ? "Join" : "Race",
        color: joining ? "#1b7a3a" : "#2e7d32",
        run: () => this.doRace(row.name),
      },
    ];
  }

  render(ctx: CanvasRenderingContext2D, _alpha: number): void {
    drawSceneBackground(ctx);

    drawSceneTitle(ctx, "FRIENDS");
    ctx.fillStyle = "#9fb0c8";
    ctx.font = "11px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("online friends can be battled or raced", VIRTUAL_WIDTH / 2, 58);

    if (!this.ctxMgr.settings.isLoggedIn()) {
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText("Log in to add friends.", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT / 2);
      return;
    }

    const rows = this.rows();
    if (rows.length === 0) {
      ctx.font = "13px system-ui, sans-serif";
      ctx.fillText("No friends yet — tap “+ Add friend”.", VIRTUAL_WIDTH / 2, LIST_TOP + 40);
      return;
    }

    ctx.save();
    ctx.beginPath();
    ctx.rect(CARD_X - 2, LIST_TOP, CARD_W + 4, LIST_H);
    ctx.clip();
    const stride = ROW_H + ROW_GAP;
    for (let i = 0; i < rows.length; i += 1) {
      const rowY = LIST_TOP + i * stride - this.scrollY;
      if (rowY + ROW_H < LIST_TOP || rowY > LIST_TOP + LIST_H) continue;
      this.drawRow(ctx, rows[i]!, rowY);
    }
    ctx.restore();

    if (this.maxScroll > 0) {
      const trackX = CARD_X + CARD_W + 4;
      const thumbH = Math.max(20, (LIST_H / (this.maxScroll + LIST_H)) * LIST_H);
      const t = this.scrollY / this.maxScroll;
      ctx.fillStyle = "rgba(245,233,200,0.25)";
      ctx.fillRect(trackX, LIST_TOP, 3, LIST_H);
      ctx.fillStyle = "rgba(245,233,200,0.85)";
      ctx.fillRect(trackX, LIST_TOP + t * (LIST_H - thumbH), 3, thumbH);
    }
  }

  private drawRow(ctx: CanvasRenderingContext2D, row: Row, rowY: number): void {
    drawCard(ctx, CARD_X, rowY, CARD_W, ROW_H, 10);

    let nameX = CARD_X + 12;
    if (row.kind === "friend") {
      // online/offline dot
      const dx = CARD_X + 16;
      const dy = rowY + ROW_H / 2;
      ctx.fillStyle = row.info.online ? "#2ecc40" : "#8a8a8a";
      ctx.beginPath();
      ctx.arc(dx, dy, 5, 0, Math.PI * 2);
      ctx.fill();
      if (row.info.online) {
        ctx.strokeStyle = "rgba(46,204,64,0.5)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(dx, dy, 7.5, 0, Math.PI * 2);
        ctx.stroke();
      }
      nameX = CARD_X + 30;
    }

    // Truncate name + status so neither runs under the row's action buttons.
    const rowBtns = this.rowButtons(row, rowY);
    const leftBtnX = rowBtns.length
      ? Math.min(...rowBtns.map((b) => b.rect.x))
      : CARD_X + CARD_W - 12;
    const textMaxW = leftBtnX - nameX - 8;

    ctx.fillStyle = "#1a1f2b";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.font = "bold 14px system-ui, sans-serif";
    ctx.fillText(truncateToWidth(ctx, row.name, textMaxW), nameX, rowY + 17);

    ctx.font = "12px system-ui, sans-serif";
    ctx.fillStyle = "#7a5e2a";
    ctx.fillText(truncateToWidth(ctx, this.statusText(row), textMaxW), nameX, rowY + 36);
    ctx.textBaseline = "alphabetic";

    for (const b of rowBtns) {
      drawPillButton(ctx, b.label, b.color, b.rect);
    }
  }

  private statusText(row: Row): string {
    if (row.kind === "in") return "wants to be friends";
    if (row.kind === "out") return "request sent — pending";
    if (row.kind === "friend" && (row.info.unread ?? 0) > 0) {
      const n = row.info.unread ?? 0;
      return `${n} new message${n > 1 ? "s" : ""} — tap to read`;
    }
    if (row.info.inviteFromThem) return "invited you to race!";
    return row.info.online ? "online · tap to message" : "offline · tap to message";
  }

  private async doAdd(): Promise<void> {
    const raw = window.prompt("Add friend (exact name):", "") ?? "";
    const name = sanitizeName(raw);
    if (!name) return;
    if (!isValidName(name)) {
      window.alert("That's not a valid player name.");
      return;
    }
    if (name.toLowerCase() === this.myName.toLowerCase()) {
      window.alert("You can't friend yourself.");
      return;
    }
    this.acting = true;
    const res = await this.ctxMgr.friends
      .request(this.myName, this.token, name)
      .finally(() => {
        this.acting = false;
      });
    if (!res.ok) {
      window.alert(
        res.error === "not_found" ? `No player named "${name}".` : "Couldn't send the request.",
      );
    }
    // request() already applied the fresh snapshot to the cache; just redraw.
    if (this.entered) this.rebuild();
  }

  private async act(kind: "accept" | "decline" | "cancel" | "remove", name: string): Promise<void> {
    const f = this.ctxMgr.friends;
    if (kind === "remove" && !window.confirm(`Remove "${name}" from your friends?`)) return;
    // Block the poll while in flight so it can't read stale mid-mutation state;
    // the mutation response already refreshes the cache.
    this.acting = true;
    const call =
      kind === "accept"
        ? f.accept(this.myName, this.token, name)
        : kind === "decline"
          ? f.decline(this.myName, this.token, name)
          : kind === "cancel"
            ? f.cancelRequest(this.myName, this.token, name)
            : f.remove(this.myName, this.token, name);
    await call.finally(() => {
      this.acting = false;
    });
    if (this.entered) this.rebuild();
  }

  private async doBattle(friend: string): Promise<void> {
    const raw = window.prompt(`Battle ${friend} — bet how many flies? (you have ${this.ctxMgr.shop.wallet})`, "100");
    if (raw === null) return;
    const bet = Math.floor(Number(raw));
    if (!Number.isFinite(bet) || bet <= 0) {
      window.alert("Enter a positive number of flies to bet.");
      return;
    }
    if (bet > this.ctxMgr.shop.wallet) {
      window.alert("You don't have that many flies.");
      return;
    }
    const res = await this.ctxMgr.battles.create(this.myName, this.token, friend, bet);
    if (!res.ok) {
      window.alert(
        res.error === "insufficient_funds"
          ? "Not enough flies for that bet."
          : res.error === "opponent_not_found"
            ? `Couldn't find "${friend}".`
            : "Couldn't start the battle.",
      );
      return;
    }
    // Show it in the Battles screen so they can track the challenge.
    this.ctxMgr.goTo(new BattlesScene(this.ctxMgr));
  }

  private doRace(friend: string): void {
    // Friendly live race — the endpoint creates an invite or joins theirs.
    this.ctxMgr.goTo(new RaceScene(this.ctxMgr, null, friend));
  }

  private openDM(friend: string): void {
    this.ctxMgr.goTo(
      new ChatScene(this.ctxMgr, dmChannel(this.myName, friend), friend, () =>
        this.ctxMgr.goTo(new FriendsScene(this.ctxMgr)),
      ),
    );
  }

  private rebuild(): void {
    const buttons: Button[] = [];
    buttons.push(backButton(() => this.ctxMgr.goTo(new MenuScene(this.ctxMgr))));

    if (this.ctxMgr.settings.isLoggedIn()) {
      const nb = { x: CARD_X, y: 74, w: CARD_W, h: 34 };
      buttons.push({
        ...nb,
        draw: (ctx, hit) => drawWideButton(ctx, "+ Add friend", nb, hit),
        onPress: () => void this.doAdd(),
      });
    }

    const rows = this.rows();
    const stride = ROW_H + ROW_GAP;
    this.maxScroll = Math.max(0, rows.length * stride - ROW_GAP - LIST_H);
    if (this.scrollY > this.maxScroll) this.scrollY = this.maxScroll;

    rows.forEach((row, i) => {
      const rowY = LIST_TOP + i * stride - this.scrollY;
      // Tapping a friend's name/left area opens a DM with them.
      if (row.kind === "friend" && rowY >= LIST_TOP && rowY + ROW_H <= LIST_TOP + LIST_H) {
        buttons.push({
          x: CARD_X,
          y: rowY,
          w: CARD_W - 162,
          h: ROW_H,
          draw: () => undefined,
          onPress: () => this.openDM(row.name),
        });
      }
      for (const b of this.rowButtons(row, rowY)) {
        if (b.rect.y < LIST_TOP || b.rect.y + b.rect.h > LIST_TOP + LIST_H) continue;
        buttons.push({ ...b.rect, draw: () => undefined, onPress: b.run });
      }
    });

    this.ctxMgr.buttons.set(buttons);
  }

  private readonly onWheel = (e: WheelEvent): void => {
    if (this.maxScroll <= 0) return;
    e.preventDefault();
    this.scrollY = clamp(this.scrollY + e.deltaY, 0, this.maxScroll);
    this.rebuild();
  };

  private readonly onDragStart = (e: PointerEvent): void => {
    if (this.maxScroll <= 0) return;
    const { x, y } = this.ctxMgr.renderer.clientToCanvas(e.clientX, e.clientY);
    if (x >= CARD_X && x <= CARD_X + CARD_W && y >= LIST_TOP && y <= LIST_TOP + LIST_H) {
      this.dragLastY = e.clientY;
    }
  };

  private readonly onDragMove = (e: PointerEvent): void => {
    if (this.dragLastY === null) return;
    const dyClient = e.clientY - this.dragLastY;
    this.dragLastY = e.clientY;
    const rect = this.ctxMgr.renderer.canvas.getBoundingClientRect();
    const dy = (dyClient / rect.height) * VIRTUAL_HEIGHT;
    this.scrollY = clamp(this.scrollY - dy, 0, this.maxScroll);
    this.rebuild();
  };

  private readonly onDragEnd = (): void => {
    this.dragLastY = null;
  };
}

function drawWideButton(
  ctx: CanvasRenderingContext2D,
  label: string,
  r: { x: number; y: number; w: number; h: number },
  hit: boolean,
): void {
  ctx.fillStyle = hit ? "#7a5e2a" : "#f5e9c8";
  fillRoundedRect(ctx, r.x, r.y, r.w, r.h, 8);
  ctx.strokeStyle = "#7a5e2a";
  ctx.lineWidth = 2;
  strokeRoundedRect(ctx, r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1, 8);
  ctx.fillStyle = hit ? "#f5e9c8" : "#7a5e2a";
  ctx.font = "bold 15px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, r.x + r.w / 2, r.y + r.h / 2);
  ctx.textBaseline = "alphabetic";
}


function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
